import { checkPermissions, useLocalStorage } from "@elit-hair/elit-tools";
import { GridColDef, GridRowId, useGridApiRef } from "@mui/x-data-grid-premium";
import { DateTime } from "luxon";
import { ReactNode, createContext, useCallback, useContext, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";

import orderColumn from "@/components/table/orderColumn";
import { LocaleStorageKeys } from "@/constants";
import { PERMISSIONS } from "@/constants/permissions";
import { DRIVER_STATUS } from "@/constants/status";
import useDriversContext from "@/providers/data/DriversContext";
import useAuth from "@/providers/hooks/useAuth";
import { useGetDailyStatusList } from "@/requests/daily-status.queries";
import { useGetVehiclesQuery } from "@/requests/transport.queries";
import { WeTrackVehicleData } from "@/types/wetrack.types";

import { CommonFilterSwitch } from "../Plannn/types";
import { DriverColumn } from "../Vehicles/VehicleTable/columns/DriverColumn";
import { PlateColumn } from "../Vehicles/VehicleTable/columns/PlateColumn";
import UserTypeColumn from "../users/components/table/components/UserTypeColumn";
import { DriverStatusColumn } from "./DriverStatusTable/components/DriverStatusColumn";
import { DriverStatusContextType, DriverStatusFilterType, DriverStatusType } from "./types";

const DriverStatusContext = createContext<DriverStatusContextType | null>(null);

export const DriverStatusProvider = ({ children }: { children: ReactNode }) => {
    const { t } = useTranslation();
    const [searchParams, setSearchParams] = useSearchParams();
    const searchParamsDate = searchParams.get("date");
    const [month, setMonth] = useState(
        searchParamsDate ? DateTime.fromFormat(searchParamsDate, "yyyy-MM") : DateTime.now().startOf("month")
    );
    const [hoveredIndex, setHoveredIndex] = useState(0);
    const [filters, setFilters] = useState<DriverStatusFilterType>({
        working: CommonFilterSwitch.ALL,
    });
    const handleFilters = useCallback(
        (newFilters: Partial<DriverStatusFilterType>) => {
            setFilters((prev) => ({ ...prev, ...newFilters }));
        },
        [setFilters]
    );
    const handlehoveredIndex = useCallback((index: number) => setHoveredIndex(index), []);
    const { userData } = useAuth();
    const pagePermissions = useMemo(
        () => ({
            edit: checkPermissions(userData?.permissionCodes || [], [PERMISSIONS.TR_SVC_HR_MANAGE_DAILY_STATUS]),
        }),
        [userData]
    );

    const [hiddenDrivers, setHiddenDrivers] = useLocalStorage<GridRowId[]>(LocaleStorageKeys.HIDDEN_DRIVERS, []);
    if (!Array.isArray(hiddenDrivers)) {
        setHiddenDrivers([]);
    }

    const updateMonth = (time: DateTime) => {
        setMonth(time);
        const date = time.toFormat("yyyy-MM");

        setSearchParams(
            { date },
            {
                replace: true,
            }
        );
    };
    const handleHiddenDrivers = (val?: GridRowId[]) => setHiddenDrivers(val ?? []);

    const { data, refetch, isPending: driverStatusLoading, isFetching } = useGetDailyStatusList({ month });
    const { data: vehicleData, isLoading } = useGetVehiclesQuery(false);

    const vehicles = useMemo(() => (vehicleData ? vehicleData.map((item) => item.properties) : []), [vehicleData]);
    const { allDrivers: drivers } = useDriversContext();

    const loading = isLoading || isFetching || driverStatusLoading;
    const api = useGridApiRef();

    const monthDays = month.daysInMonth || DateTime.now().daysInMonth;

    const allRows = useMemo<DriverStatusType[]>(() => {
        if (!monthDays || !data) return [];
        const startofMonth = month.startOf("month");
        const endOfMonth = month.endOf("month");
        return drivers
            .filter((item) => {
                if (!item.lifecycles) return false;
                const activeLifeCycle = item.lifecycles.at(-1);
                if (hiddenDrivers.includes(item.id) || !activeLifeCycle) return false;

                const { leavedAt, startedAt } = activeLifeCycle;
                const startedAtDate = startedAt ? DateTime.fromISO(startedAt) : undefined;
                const leavedAtDate = leavedAt ? DateTime.fromISO(leavedAt) : undefined;

                return (!startedAtDate || startedAtDate < endOfMonth) && (!leavedAtDate || leavedAtDate > startofMonth);
            })
            .map((driver) => {
                let includesActiveStatus = false;
                const baseData: DriverStatusType = {
                    id: driver.id,
                    name: driver.name,
                    isDeleted: driver.isDeleted,
                    plate: driver.vehicleData?.licensePlate || "",
                    vehicleId: driver.vehicleData?.id ? +driver.vehicleData?.id : undefined,
                    driverId: driver.driverId,
                    userType: driver.userType,
                    extras: driver.extras || 0,
                    includesActiveStatus: false,
                };
                const driverStatusReports = data.filter(
                    (status) => status.userId === driver.id || (driver.driverId && +driver.driverId === status.driverId)
                );

                for (let i = 1; i <= monthDays; i++) {
                    baseData[`day${i}` as keyof typeof baseData] = driverStatusReports.filter((status) => {
                        const includeItem =
                            status.startTime.day === i ||
                            (status.status != DRIVER_STATUS.WORKING &&
                                status.status != DRIVER_STATUS.FREE_DAY &&
                                status.endTime &&
                                status.endTime.day >= i &&
                                status.startTime.day <= i);

                        if (includeItem && status.isActive && status.status === DRIVER_STATUS.WORKING) {
                            includesActiveStatus = true;
                        }
                        return includeItem;
                    });
                }

                return {
                    ...baseData,
                    includesActiveStatus,
                };
            });
    }, [drivers, data, month, hiddenDrivers, monthDays]);

    const rows = useMemo<DriverStatusType[]>(() => {
        if (!allRows) return [];
        const { working } = filters;
        if (!working) return allRows;
        return allRows.filter((user) => {
            if (
                (user.includesActiveStatus && working === CommonFilterSwitch.TRUE) ||
                (!user.includesActiveStatus && CommonFilterSwitch.FALSE === working)
            )
                return true;
        });
    }, [filters, allRows]);
    const columns: GridColDef[] = useMemo(() => {
        const list: GridColDef[] = [
            orderColumn(),
            DriverColumn({ avatar: true }),
            UserTypeColumn({ t }),
            PlateColumn<WeTrackVehicleData["properties"]>({ getPlate: (row) => row.plate || "" }),
        ];

        if (!monthDays) return list;

        for (let i = 1; i <= monthDays; i++) {
            list.push(DriverStatusColumn(i));
        }

        return list;
    }, [rows, month, drivers, data, monthDays, t]);

    return (
        <DriverStatusContext.Provider
            value={{
                api,
                loading,
                columns,
                rows,
                allRows,
                reloadTable: refetch,
                updateMonth,
                month,
                vehicles,
                hiddenDrivers,
                handleHiddenDrivers,
                handlehoveredIndex,
                hoveredIndex,
                pagePermissions,
                filters,
                handleFilters,
            }}
        >
            {children}
        </DriverStatusContext.Provider>
    );
};
export const useDriverStatusContext = () => {
    const ctx = useContext(DriverStatusContext);
    if (!ctx) {
        throw new Error("useVehiclesContext must be used within");
    } else {
        return ctx;
    }
};

export default DriverStatusContext;
